<template>
    <div id="pathways-push-segment" class="position-relative">
        <div id="choose" :class="stage > 0 ? 'visually-hidden' : ''">
            <div id="choose-segment" class="card">
                <div id="choose-segment__description">
                    <h4 class="mb-1">Step 1: Select Audience</h4>
                    <p>Select audience to push to a destination.</p>
                </div>

                <div id="choose-segment__filter-row" class="d-flex flex-column flex-sm-row gap-2 align-items-stretch">
                    <div class="search-box flex-grow-1 position-relative">
                        <input type="text" class="form-control" placeholder="Search by Audience Name..." aria-label="Search by Audience Name..." v-model="search_key" @input="handleFilterSelection()">
                        <i class="fa fa-search"></i>
                    </div>
                    <div class="d-flex gap-2 justify-content-end">
                        <button class="btn btn-secondary dropdown-toggle dropdown-toggle-no-btn" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                            <img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">
                            <span v-if="window_width > 880">Filter</span>
                        </button>
                        <div class="dropdown-menu dropdown-menu-primary  dropdown-menu-end p-3 overflow-visible" style="min-width: 250px;">
                            <h4>Filters</h4>
                            
                            <!-- Source Filter AccordionMultiselect -->
                            <div class="mb-3">
                                <label class="form-label">Source</label>
                                <AccordionMultiSelect
                                    :options="source_list"
                                    v-model="filters.source"
                                    @update:modelValue="handleFilterSelection"
                                    placeholder="Select sources..."
                                />
                            </div>

                            <!-- Audience Type Filter AccordionMultiselect -->
                            <div class="mb-3">
                                <label class="form-label">Audience Type</label>
                                <AccordionMultiSelect
                                    :options="dest_list"
                                    v-model="filters.destination"
                                    @update:modelValue="handleFilterSelection"
                                    placeholder="Select audience types..."
                                />
                            </div>
                        </div>

                        <button type="button" class="btn btn-primary btn-icon-only-md" data-bs-toggle="tooltip" title="Add an Audience" @click="openDescribeSegmentModal">
                            <img class="icon pe-0" style="width: 20px; height: 20px;" src="/img/icons/avid-icons/edna.svg" alt="">
                        </button>
                    </div>
                </div>

                <div id="choose-segment__segments-list" class="d-flex flex-column">
                    <div v-if="segment_list_shown === null || segment_list_shown.length === 0" class="card long-card disabled">
                        <div class="card-body long-card__body row">
                            <div class="long-card__content">
                                <h4 class="mb-1">No Segments Created</h4>
                                <p>To push a segment, you first need to create one using existing data sets or data blends.
                                    <button type="button" class="btn btn-link p-0 align-baseline" @click="openDescribeSegmentModal">Click here to do so.</button>
                                </p>
                            </div>
                        </div>
                    </div>
                    <div v-else v-for="(segment, seg_index) in segment_list_shown" :key="segment.id" class="card long-card" :class="segment.id === segment_selected.id ? 'long-card-selected' : ''"
                    @click="changeSegmentSelection(segment)">
                        <div class="card-body long-card__body d-flex align-items-center">
                            <!-- <div class="long-card__radio d-flex">
                                <input class="form-radio-input" type="radio" :id="segment.id" :value="segment.id" :checked="segment.id === segment_selected.id" aria-label="Radio option">
                            </div> -->

                            <div class="long-card__content d-flex flex-row justfy-content-start gap-2">
                                <div class="d-flex flex-column flex-grow-1 gap-1">
                                    <div class="d-block">
                                        <div class="d-flex flex-row align-items-center position-relative">
                                            <h4>{{ segment.name }}</h4>
                                            <div v-if="segment.sizes" class="w-100">
                                                {{  number_with_zero(getDefaultSize(segment.sizes)) }} Contacts 
                                                <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" title="The actual number of records synced to the destination will vary based upon the primary key selected when setting up the sync." alt=""></img>
                                                
                                                <small v-if="segment.dataset" class="ms-2">
                                                    <span class="badge">{{ segment.dataset.name}}</span>
                                                </small>
                                                <small v-else-if="segment.datablend" class="ms-2">
                                                    <span class="badge">{{ segment.datablend.name}}</span>
                                                </small>
                                                <small v-else-if="segment.data_source" class="ms-2">
                                                    <span class="badge">{{ segment.data_source.name}}</span>
                                                </small>
                                            </div>
                                            <div v-if="segment.type == 'modeled'" class="w-100">
                                                <small v-if="segment.type === 'modeled'" class="">
                                                    <span class="badge">Modeled Audience</span>
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="table-pagination mt-auto">
                        <div class="table-pagination--group d-none d-md-flex">
                            <div class="position-relative">
                                <select class="form-select" name="num_per_page" id="num_per_page" v-model="num_per_page" @change="reloadPagination()" aria-label="Num Per Page">
                                    <option value="5000">ALL</option>
                                    <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                                </select>
                                <div class="select-display-text">
                                    Showing {{ num_per_page === '5000' ? 'ALL' : num_per_page }}
                                </div>
                            </div>
                        </div>

                        <div class="table-pagination--group d-none d-lg-flex">
                            {{ filtered_segment_list.length === 0 ? '0' : item_interval }} of {{ filtered_segment_list.length }}
                        </div>

                        <div class="table-pagination--group">
                            <button v-if="page_count>1" class="btn btn-secondary btn-icon-only-md me-1" @click="moveToPrevPage()" :disabled="page_index === 0" data-bs-toggle="tooltip" title="Previous page">
                                <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="">
                            </button>

                            <div class="d-flex page-selector" v-if="page_index_array.length<=3">
                                <button v-for="page in page_index_array" :key="page" class="btn btn-none btn-icon-only-md" :class="page === page_index + 1 ? 'current-page':''" @click="moveToPage(page)">
                                {{page}}
                                </button>
                            </div>
                            <div class="d-flex page-selector" v-else>
                                <button v-for="page in page_index_array.slice(0,2)" :key="page" class="btn btn-none btn-icon-only-md" :class="page === page_index + 1 ? 'current-page':''" @click="moveToPage(page)">
                                {{page}}
                                </button>

                                <button v-if="page_index > 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                                ...
                                </button>
                                <button v-if="page_index >= 2 && page_index< page_index_array.length -1" class="btn btn-none btn-icon-only-md current-page">
                                    {{page_index + 1 }}
                                </button>

                                <button v-if="page_index < page_index_array.length -2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                                ...
                                </button>
                                <button :class="page_index === page_index_array.length -1 ? 'current-page':''" class="btn btn-none btn-icon-only-md" @click="moveToPage(page_index_array.length)" :key="page_index_array.length">
                                {{ page_index_array.length }}
                                </button>
                            </div>

                            <button v-if="page_count>1" class="btn btn-secondary btn-icon-only-md ms-1" @click="moveToNextPage()" :disabled="page_index === page_count -1" data-bs-toggle="tooltip" title="Next page">
                                <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="">
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div id="choose-pathway" class="card">
                <div id="choose-pathway__description">
                    <h4 class="mb-1">Step 2: Choose a Destination</h4>
                    <p v-if="segment_selected.id === undefined"><img class="icon pe-2" src="/img/icons/avid-icons/warning-circle.svg" alt="">Select your audience before you chose the pathway</p>
                    <p v-else>Now you can select a pathway</p>
                </div>

                <div id="choose-pathway__pathways-list" class="flex-grow-1 position-relative" :style="{ minHeight: calculatePathwaysListHeight() + 'px' }">
                    <div class="position-relative h-100 d-flex flex-column">
                        <div v-if="connection_datasets.length === 0" class="card long-card disabled">
                            <div class="card-body long-card__body row">
                                <div class="long-card__content">
                                    <h4 class="mb-1">No Pathway Integrations Set Up</h4>
                                    <p>Before you can push data, <a :href="'/settings/'+client.url+'/integrations'">you first need to connect integrations that have valid Pathways</a>. <em>(If you have integrations but they are not showing, they are not available for Pathways yet.)</em></p>
                                </div>
                            </div>
                        </div>
                        <div class="scroll-view d-flex flex-column flex-shrink-1" style="max-height: 100%;" :class="connection_datasets.length >= 7 ? 'pe-2' : ''">
                            <div v-for="(conn, conn_index) in connection_datasets" :key="conn.id" class="card long-card" :class="getPathwayCardClass(conn)" @click="changePathwaySelection(conn)">
                                <div class="card-body long-card__body d-flex align-items-center">
                                    <!-- <div class="long-card__radio d-flex">
                                        <input class="form-radio-input" type="radio" :id="conn.id" :value="conn.id" :checked="conn.id === pathway_selected.id"
                                        :disabled="segment_selected.id === undefined || isAlreadySynced(conn)" aria-label="Radio option">
                                    </div> -->

                                    <div class="long-card__content d-flex flex-row justfy-content-start gap-2 align-items-center">
                                        <div class="d-flex flex-column gap-1">
                                            <img :src="conn.integration.logo_url" :alt="conn.integration.name" class="avatar rounded-circle avatar-sm bg-white">
                                        </div>
                                        <div class="d-flex flex-column gap-1">
                                            <h4>{{conn.integration_id === '71b0c3b0-8390-49ec-9984-1c04b7384d38' ? 'File Export' : conn.name}}</h4>
                                            <em class="text-muted" v-if="isAlreadySynced(conn)">(Already Pushed)</em>
                                            <em class="text-muted" v-if="isSegmentTypeNotAllowed(conn)">You cannot push modeled audiences to this destination</em>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="d-flex justify-content-end mt-5 mt-sm-4" v-if="stage === 0">
                            <div>
                                <button class="btn btn-primary btn-lg float-end" :disabled="segment_selected.id === undefined || pathway_selected.id === undefined" @click.prevent="proceedToSummary()">
                                    Review Pathway
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div id="summary" :class="stage > 0 ? '' : 'd-none'">

            <pathways-push-segment-summary v-if="stage !== 0" :segment_selected="segment_selected" :pathway_selected="pathway_selected" :client="client" ref="pathway_summary"
            :census_connection="local_census_connection" @updateCensusConnection="updateCensusConnection" @persistDefaultedDestinationObject="persistCensusConnection" :information_errors="information_errors"></pathways-push-segment-summary>
        </div>

        <div  v-if="stage > 0">
            
            <nav class="navbar navbar-expand py-0 row d-block d-md-flex justify-content-between">
                
                <div class="col-12 d-flex justify-content-end">
                    <button class="btn btn-white btn-lg me-3" @click.prevent="backToSelection()" :disabled="busy">
                       <span>Cancel</span>
                    </button>
                    <button class="btn btn-primary btn-lg" v-if="stage === 1" :disabled="true">
                        <div class="spinner-border spinner-border-sm me-2" role="status"><span class="visually-hidden"></span></div> Validating
                    </button>
                    <button class="btn btn-primary btn-lg" v-if="stage === 2" :disabled="true">
                        Need Attention
                    </button>
                    <button class="btn btn-primary btn-lg" v-if="stage === 3" @click.prevent="saveAndPush()" :disabled="busy">
                        <span v-if="!busy">Confirm</span>
                        <span v-else><span class="spinner-border spinner-border-sm me-2" role="status"><span class="visually-hidden"></span></span> Processing</span>
                    </button>
                </div>
            </nav>
        </div>

        <DescribeSegmentModal
            ref="describeSegmentModalRef"
            :client="client"
            modal-id="pathways-push-describe-segment-modal"
            @submitted="onSegmentSuggestionSubmitted"
            @error="onSegmentSuggestionError"
        />
        <SegmentProposalModal
            v-if="client"
            ref="segmentProposalModalRef"
            :client="client"
            @saved="onSegmentProposalSaved"
        />
    </div>
</template>

<script>
    import Swal from 'sweetalert2';
    import { defineComponent } from 'vue';
    import AccordionMultiSelect from '../components/AccordionMultiSelect.vue';
    import DescribeSegmentModal from './DescribeSegmentModal.vue';
    import SegmentProposalModal from './SegmentProposalModal.vue';
    import { useNotifications } from '../../composables/useNotifications';
    import { useAISuggestionPolling, createSegmentSuggestionCheckFunction } from '../../composables/useAISuggestionPolling';

    export default defineComponent({
        components: {
            Swal,
            AccordionMultiSelect,
            DescribeSegmentModal,
            SegmentProposalModal,
        },
        setup() {
            const { showNotification } = useNotifications();
            const { startPolling, stopPolling } = useAISuggestionPolling();
            return { showNotification, startPolling, stopPolling };
        },
        props: ['connection_datasets', 'client', 'segments','datasets', 'datablends', 'census_connections',
        'segment_selected_passed', 'pathway_selected_passed'],
        data() {
            return {
                local_census_connection: {},
                stored_census_connection: null,
                filtered_segment_list: [],
                segment: {
                    name: '',
                    data_source_id: null,
                    filter: null
                },
                segment_list_shown:[],
                search_key: '',
                filters: {},
                segment_selected: {},
                pathway_selected: {},
                stage: 0,
                // Error handling in advance settings
                information_errors:{
                    destination_object: "",
                    operation: "",
                    conversion_action: "",
                    form: false
                },
                busy: false,
                // Payment confirmation state
                paymentConfirmed: false,
                selectedPaymentOption: null,

                // pagination
                num_per_page: 5, // default to 5
                num_per_page_options: [5,10,15,20,25,30,50,100],
                page_count: null,
                page_index: 0,
                page_index_array: [],
                item_interval: '',

                // Browser history support
                popstateListenerAdded: false,
                window_width: window.innerWidth,
                // Cost information loaded from API
                costInfo: { cost: 0, included: false, free: false, loading: false, payment_options: [] }
            };
        },
        beforeMount() {
            this.filtered_segment_list = this.segments;
            this.reloadPagination();
            this.setupFilter();
            if (this.segment_selected_passed && this.pathway_selected_passed){
                this.segment_selected=this.segment_selected_passed;
                this.pathway_selected=this.pathway_selected_passed;
                this.stage = 1;
            }
            if(this.showCost()) {
                this.fetchPathwayCost();
            }
        },
        mounted() {
        },
        beforeUnmount() {
            // Clean up event listener when component is destroyed
            if (this.popstateListenerAdded) {
                window.removeEventListener('popstate', this.handlePopState);
            }
        },
        updated() {
            const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
            const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

            for (var i = 0; i < tooltipTriggerList.length; i++) {
              (function(index) {
                tooltipTriggerList[index].addEventListener('click', function() {
                  // Hide the tooltip using the index passed to the closure
                  tooltipList[index].hide();
                });
                tooltipTriggerList[index].addEventListener('mouseleave', function() {
                  // Hide the tooltip using the index passed to the closure
                  tooltipList[index].hide();
                });
              })(i);
            }
        }, 
        computed: {
            costText() {
                if(this.costInfo.loading) return 'Loading...';
                if(this.costInfo.cost === 0) {
                    return this.costInfo.free ? 'Free' : 'None';
                }
                return '$' + this.costInfo.cost + ' / month <em>(Includes unlimited Pathways and Playbook deployment)</em>';
            },
            source_list() {
                // Get unique sources from segments
                const usedSources = new Set();
                this.segments.forEach(seg => {
                    let data_source = seg.dataset ? seg.dataset : seg.datablend;
                    if(!data_source)
                        data_source = seg.data_source ? seg.data_source : {};
                    
                    if(data_source && data_source.name) {
                        usedSources.add(data_source.name);
                    }
                });
                return Array.from(usedSources).sort().map(source => ({
                    value: source,
                    text: source,
                    id: source
                }));
            },
            dest_list() {
                // Get unique audience types from segments
                const usedConnections = new Set();
                this.segments.forEach(seg => {
                    if (seg.pushed_pathways) {
                        seg.pushed_pathways.forEach(pathway => {
                            if (pathway && pathway.conn_name && pathway.conn_name.indexOf('File') === -1) {
                                usedConnections.add(pathway.conn_name);
                            }
                        });
                    }
                });

                // Add special audience types
                const destinations = Array.from(usedConnections);
                destinations.push('Analysis Audience');

                return destinations.map(dest => ({
                    value: dest,
                    text: dest,
                    id: dest
                }));
            }
        },
        methods: {
            openDescribeSegmentModal() {
                this.$refs.describeSegmentModalRef?.show();
            },
            onSegmentSuggestionSubmitted({ segment_suggestion_id }) {
                this.showNotification({
                    icon: '/img/icons/nav-icons/Edna.svg',
                    text: "We're creating your audience. You'll be notified when it's ready!",
                    type: 'info',
                    noDismiss: true,
                });
                const requestKey = `segment-suggestion-${segment_suggestion_id}`;
                this.startPolling({
                    requestKey,
                    checkFn: createSegmentSuggestionCheckFunction({
                        clientUrl: this.client.url,
                        segmentSuggestionId: segment_suggestion_id,
                    }),
                    onSuccess: (data) => {
                        this.showNotification({
                            icon: '/img/icons/nav-icons/Edna.svg',
                            text: "Your audience suggestion is ready. Click to view.",
                            type: 'success',
                            action: { type: 'openSegmentProposal', segment_suggestion_id: data.id },
                        });
                    },
                    onTimeout: () => {},
                });
            },
            onSegmentSuggestionError(message) {
                this.showNotification({ text: message || 'Failed to submit.', type: 'error' });
            },
            onSegmentProposalSaved({ segment }) {
                if (segment && this.filtered_segment_list) {
                    this.filtered_segment_list.unshift(segment);
                    this.reloadPagination();
                }
            },
            showCost() {
                return !this.costInfo.loading && this.pathway_selected && this.pathway_selected.id !== undefined;
            },
            fetchPathwayCost() {
                if(!this.pathway_selected.id) return;
                this.costInfo.loading = true;
                window.axios.get('/api/pathways/'+this.client.url+'/'+this.pathway_selected.id+'/cost')
                    .then(response => {
                        this.costInfo = { ...response.data, loading: false };
                    })
                    .catch(() => { this.costInfo.loading = false; });
            },
            getDefaultSize(sizes) {
                for (var i = 0; i < sizes.length; i++){
                    if (sizes[i].id == 0)
                        return sizes[i].size;
                }
                return null
            },
            proceedToSummary(){
                const isAvidIntegration = this.pathway_selected.integration.census_connection_type === 'avid';
                if(!isAvidIntegration && this.pathway_selected.census_destination_id == null && this.pathway_selected.integration.census_requires_oauth){
                    // census oauth
                    var self = this;
                    Swal.fire({
                        title: "Additional Access is Needed",
                        text: "Avid has partnered with a software service named Census to activate the ability to push data to "+this.pathway_selected.integration.display_name+". This destination requires additional authentication before it can be used.",
                        iconHtml: '<i class="fas fa-exclamation"></i>',
                        showCancelButton: true,
                        confirmButtonText: "Proceed",
                        customClass: {
                            popup: 'swal-warning'
                        },
                        }).then((result) => {
                            if (result.isConfirmed) {
                                window.location = '/integrations/'+this.client.url+'/census/oauth/' + self.pathway_selected.id + '/'+ self.segment_selected.id;
                            }
                        });
                }
                else{
                    // Save current state to allow browser back button to work
                    var newURL = '/pathways/'+this.client.url +'/'+this.segment_selected.id+'/'+this.pathway_selected.id;
                    window.history.pushState({
                        stage: 1,
                        segment_id: this.segment_selected.id,
                        pathway_id: this.pathway_selected.id
                    }, '', newURL);

                    this.stage = 1;

                    // Add event listener for popstate if not already added
                    if (!this.popstateListenerAdded) {
                        window.addEventListener('popstate', this.handlePopState);
                        this.popstateListenerAdded = true;
                    }
                }
            },
            setupFilter(){
                // Initialize filters with no options selected (empty arrays)
                this.filters = {
                    source: [],
                    destination: []
                }
            },
            handleFilterSelection(){
                var filtered_list = [];

                if (this.segments.length === 0) {
                    return;
                }
                
                // If no filters are selected, show all items (only apply search filter)
                const noSourceFilter = this.filters.source.length === 0;
                const noDestinationFilter = this.filters.destination.length === 0;
                
                this.segments.forEach(seg => {
                    let data_source = seg.dataset ? seg.dataset : seg.datablend;
                    if(!data_source)
                        data_source = seg.data_source ? seg.data_source : {};

                    let in_search = this.search_key === '' || 
                        seg.name.toLowerCase().includes(this.search_key.toLowerCase());

                    // Check source filter (if any source filters are selected)
                    let source_match = noSourceFilter || this.filters.source.some(filter => filter.value === data_source.name);
                    
                    if (in_search && source_match) {
                        // Check destination filter
                        if (noDestinationFilter) {
                            // No destination filters selected, include all segments
                            filtered_list.push(seg);
                        } else {
                            // Check if segment matches any selected destination filters
                            let destination_match = false;
                            
                            this.filters.destination.forEach(dest => {
                                // Check special audience types
                                if (dest.value === 'Analysis Audience' && seg.analysis !== null) {
                                    destination_match = true;
                                } else if (Array.isArray(seg.pushed_pathways) && seg.pushed_pathways.some(pathway => 
                                    pathway && pathway.conn_name && pathway.conn_name === dest.value
                                )) {
                                    destination_match = true;
                                }
                            });
                            
                            if (destination_match) {
                                filtered_list.push(seg);
                            }
                        }
                    }
                });

                this.filtered_segment_list = filtered_list;
                this.reloadPagination();
                this.moveToPage(1);
                this.$forceUpdate();
            },
            addNewSegment() {
                if(this.datablends.length > 0){
                    this.segment = {
                        id: null,
                        source: 'data_blend',
                        name: '',
                        data_source_id: this.datablends[0].id,
                        filter: null,
                        expires_at: null
                    };
                }
                else {
                    this.segment = {
                        id: null,
                        source: 'data_set',
                        name: '',
                        data_source_id: this.datasets[0].id,
                        filter: null,
                        expires_at: null
                    };
                }
            },
            addSegmentToDataSet(seg) {
                this.filtered_segment_list.push(seg);
                // this.sortDataWithLabel();
                this.reloadPagination();
            },
            reloadPagination(){
                this.page_count = Math.ceil(this.filtered_segment_list.length / this.num_per_page);
                // this.page_index = 0;
                this.page_index_array = Array.from({length: this.page_count}, (_, i) => i + 1);
                this.getItemInterval();
            },
            getItemInterval(){
                var from = this.page_index * this.num_per_page + 1;
                var to = (this.page_index +1) * this.num_per_page;
                if(to>this.filtered_segment_list.length)
                    to = this.filtered_segment_list.length;
                if (from >= to)
                    this.item_interval = to
                else
                    this.item_interval = from +' - ' +to;

                this.segment_list_shown = this.filtered_segment_list.slice(from-1, to);
            },
            moveToPrevPage(){
                this.page_index--;
                this.getItemInterval();
            },
            moveToPage(page){
                this.page_index = page-1;
                this.getItemInterval();
            },
            moveToNextPage(){
                this.page_index++;
                this.getItemInterval();
            },
            changeSegmentSelection(segment){
                this.segment_selected = segment;
                //Determine if there are existing connections set up and disable those destinations

            },
            isAlreadySynced(conn) {
                if(conn.integration_id == '71b0c3b0-8390-49ec-9984-1c04b7384d38') return false;

                if(this.segment_selected == null || this.segment_selected.pushed_pathways == null) return false;
                //Check if the conn.name is in the this.segment_selected.pushed_pathways
                //pushed_pathways is an object and I need to compare conn.name against the conn_name attribute
                if (Object.values(this.segment_selected.pushed_pathways).some(pathway => pathway.conn_name === conn.name)) {
                    return true;
                }
                return false
            },
            isSegmentTypeNotAllowed(conn) {
                // If no segment is selected, allow all connections
                if (!this.segment_selected || !this.segment_selected.id) {
                    return false;
                }

                // Get the segment type (default to 'owned' if not set)
                const segmentType = this.segment_selected.type || 'owned';

                // Check if the integration has census_segment_types defined
                if (!conn.integration || !conn.integration.census_segment_types) {
                    return false; // If no restrictions defined, allow all
                }

                // Check if the segment type is in the allowed types
                return !conn.integration.census_segment_types.includes(segmentType);
            },
            getPathwayCardClass(conn){
                if (this.segment_selected.id == undefined  || this.isAlreadySynced(conn) || this.isSegmentTypeNotAllowed(conn)){
                    return 'long-card-disabled';
                }
                if (conn.id == this.pathway_selected.id){
                    return 'long-card-selected';
                }
            },
            changePathwaySelection(pathway){
                if (!this.segment_selected.id){
                    return;
                }
                if(this.isAlreadySynced(pathway)){
                    return;
                }
                if(this.isSegmentTypeNotAllowed(pathway)){
                    return;
                }
                this.pathway_selected = pathway;
                this.fetchPathwayCost();
                var temp = this.census_connections.filter(conn => conn.connection_id == this.pathway_selected.id && conn.data_source_id == this.segment_selected.data_source_id);
                if (temp.length >0)
                    this.local_census_connection = temp[0];
            },
            cancelSelection(){
                this.segment_selected = {};
                this.pathway_selected = {};
                this.local_census_connection = {};
                this.stage = 0;
            },
            updateCensusConnection(details){
                this.local_census_connection = details;

                //Save the initial version of the connection but don't allow it to be overwritten but future edits
                if(this.stored_census_connection == null)
                    this.stored_census_connection = JSON.parse(JSON.stringify(details));

                // For modeled audiences, skip validation and go directly to stage 3
                if(this.isModeledAudience()) {
                    this.stage = 3;
                    return;
                }

                //If there aren't any mappings, need to make them
                if(this.local_census_connection.mappings == null || this.local_census_connection.mappings.length === 0){
                    this.stage = 2;
                    return;
                }

                var unmappedFields = this.local_census_connection.mappings.filter((mapping, index) =>
                    //If the first one isn't a primary identifier
                     (index == 0 && mapping.is_primary_identifier != 1) ||
                     //If the first one is an array but the first value isn't set
                     (index == 0 && Array.isArray(mapping.source_column) && mapping.source_column.length > 0 &&
                        (this.local_census_connection.is_file == false &&
                            (mapping.source_column[0].value == null || mapping.source_column[0].value == ""))) ||
                     //If it is a normal object but the value hasn't been set
                     (mapping.source_column && !Array.isArray(mapping.source_column) && (mapping.source_column.value == null || mapping.source_column.value == ""))
                );

                //If there are still unmapped fields or the connection isn't set
                if(unmappedFields.length > 0 || !this.local_census_connection.connection_id)
                    this.stage = 2;
                else{
                    this.stage = 3;
                }

                // this.$forceUpdate();
            },
            persistCensusConnection() {
                if (!this.local_census_connection?.id) {
                    return;
                }
                const payload = { ...this.local_census_connection, save_mapping: 0 };
                window.axios.put('/api/census_connection/' + this.local_census_connection.id, payload)
                    .then(response => {
                        this.local_census_connection = response.data.census_connection;
                        this.stored_census_connection = JSON.parse(JSON.stringify(this.local_census_connection));
                    })
                    .catch(() => {});
            },
            validateCensusConnection() {
                // Skip validation for modeled audiences
                if(this.isModeledAudience()) {
                    return true;
                }
                
                this.information_errors.operation = "";
                this.information_errors.destination_object = "";
                this.information_errors.conversion_action = "";

                let is_valid = true;

                if(this.local_census_connection.operation == null || this.local_census_connection.operation == ''){
                    this.information_errors.operation = 'The method of sync is a required field';
                    is_valid = false;
                }
                if(this.local_census_connection.destination_object == null || this.local_census_connection.destination_object == '') {
                    is_valid = false;
                    this.information_errors.destination_object = 'The destination object is a required field';
                }
                const effectiveObj = this.getEffectiveDestinationObject(this.local_census_connection.destination_object);
                if (effectiveObj === 'enhanced_conversion' && this.pathway_selected?.integration?.display_name === 'Google Ads') {
                    const caId = this.getSelectedConversionActionId(this.local_census_connection.destination_object);
                    if (!caId) {
                        is_valid = false;
                        this.information_errors.conversion_action = 'Please select a conversion action';
                    }
                }

                return is_valid;
            },
            getEffectiveDestinationObject(obj) {
                if (!obj) return obj;
                if (obj === 'user_data' || obj === 'enhanced_conversion') return obj;
                try {
                    const parsed = typeof obj === 'string' ? JSON.parse(obj) : obj;
                    if (parsed && (parsed.conversion_action_id || parsed.conversionActionId)) {
                        return 'enhanced_conversion';
                    }
                } catch (_) {}
                return obj;
            },
            getSelectedConversionActionId(obj) {
                if (!obj) return '';
                try {
                    const parsed = typeof obj === 'string' ? JSON.parse(obj) : obj;
                    return parsed?.conversion_action_id || parsed?.conversionActionId || '';
                } catch (_) {
                    return '';
                }
            },
            backToSelection(){
                this.stage = 0;
                this.cancelSelection();
                this.resetView();

                this.stored_census_connection = null
                var newURL = '/pathways/'+this.client.url+"/push_audience";

                // Use pushState to ensure consistent browser history behavior
                window.history.pushState({
                    stage: 0
                }, '', newURL);
            },

            handlePopState(event) {
                // Handle browser back/forward button events
                if (event.state === null) {
                    // If no state, go back to selection screen
                    this.stage = 0;
                    this.cancelSelection();
                    this.resetView();
                } else if (event.state.stage !== undefined) {
                    // First reset the current view state
                    const previousStage = this.stage;
                    this.stage = event.state.stage;

                    // If we need to restore segment and pathway selections
                    if (this.stage === 1 && event.state.segment_id && event.state.pathway_id) {
                        // Find and restore the selected segment
                        const segment = this.segments.find(s => s.id === event.state.segment_id);
                        if (segment) {
                            this.segment_selected = segment;
                        }

                        // Find and restore the selected pathway
                        const pathway = this.connection_datasets.find(p => p.id === event.state.pathway_id);
                        if (pathway) {
                            this.pathway_selected = pathway;
                        }

                        // Reset census connection if needed
                        if (previousStage > 1) {
                            this.stored_census_connection = null;
                            this.local_census_connection = {};

                            // Find and restore the census connection if it exists
                            var temp = this.census_connections.filter(conn =>
                                conn.connection_id == this.pathway_selected.id &&
                                conn.data_source_id == this.segment_selected.data_source_id
                            );
                            if (temp.length > 0) {
                                this.local_census_connection = temp[0];
                            }
                        }
                    } else if (this.stage === 0) {
                        // Going back to selection screen
                        this.cancelSelection();
                        this.resetView();
                    }
                }

                // Force a UI update
                this.$nextTick(() => {
                    this.$forceUpdate();
                });
            },

            resetView() {
                // Reset pagination and filters to initial state
                this.filtered_segment_list = this.segments;
                this.reloadPagination();
                this.moveToPage(1);

                // Reset any other UI state as needed
                this.stored_census_connection = null;
                this.local_census_connection = {};
            },
            isFile() {
                return this.pathway_selected.integration_id == "71b0c3b0-8390-49ec-9984-1c04b7384d38";
            },
            isModeledAudience() {
                return this.segment_selected.type === "modeled";
            },
            saveAndPush() {
                // If there is an additional cost and the user has not yet confirmed, show confirmation modal
                if(this.costInfo.cost > 0 && !this.paymentConfirmed){
                    this.showPaymentConfirmation();
                    return;
                }
                var is_valid = true;
                if(this.isFile()){
                    this.local_census_connection.operation = 'create';
                    this.local_census_connection.destination_object = 'file';

                    if(this.$refs.pathway_summary.$refs.file_mapping != undefined && !this.$refs.pathway_summary.$refs.file_mapping.isValid())
                        is_valid = false;
                } else {
                    is_valid = this.validateCensusConnection();
                    // Skip column mapping validation for modeled audiences
                    if(!this.isModeledAudience() && this.$refs.pathway_summary.$refs.column_mapping != undefined && !this.$refs.pathway_summary.$refs.column_mapping.isValid())
                        is_valid = false;
                }

                if(!is_valid){
                    this.information_errors.form = true;
                }
                else {
                    this.information_errors.form = false;
                    this.busy = true;
                    this.save(true);
                }
            },
            showPaymentConfirmation(){
                // If no payment options available, prompt user to add billing information
                if(!this.costInfo.payment_options || Object.keys(this.costInfo.payment_options).length === 0){
                    Swal.fire({
                        title: 'Billing Information Required',
                        html: `<p class="mb-2">Before you can create this Pathway with Avid Relay, you need to add a payment method.</p><p><a class="btn btn-primary" href="/settings/${this.client.url}/billing">Go to your Billing Settings</a></p>`,
                        icon: 'warning',
                        showConfirmButton: false,
                        customClass: {
                            popup: 'swal-warning'
                        }
                    });
                    return;
                }
                let htmlContent = `<p>By authorizing this Pathway, you will be adding an additional $${this.costInfo.cost} per month to subsequent invoices. We call this feature "Avid Relay"; it provides unlimited Pathways and allows you to deploy Playbooks directly to your advertising and marketing systems.</p>`;
                const paymentOptions = this.costInfo.payment_options;
                const optsArray = Array.isArray(paymentOptions) ? paymentOptions : Object.entries(paymentOptions);
                if(optsArray.length > 1){
                    htmlContent += '<p>Please select which account you would like to charge.</p>';
                    optsArray.forEach((opt, idx) => {
                        const [key, value] = Array.isArray(opt) ? opt : [opt, opt];
                        htmlContent += `<div class="form-check text-start mb-2"><input class="form-check-input" type="radio" name="paymentOpt" id="paymentOpt${idx}" value="${key}" ${idx===0?'checked':''} aria-label="Paymentopt"><label class="form-check-label ms-2" for="paymentOpt${idx}">${value}</label></div>`;
                    });
                } else {
                    const [key, value] = Array.isArray(optsArray[0]) ? optsArray[0] : [optsArray[0], optsArray[0]];
                    htmlContent += `<p class="fw-bold text-center">This will be charged to ${value}</p>`;
                }
                htmlContent += `<div class="form-check mt-3 text-start"><input class="form-check-input" type="checkbox" id="confirmCharge" aria-label="Checkbox"><label class="form-check-label ms-2" for="confirmCharge">I understand I will be charged an additional $${this.costInfo.cost} per month on subsequent invoices.</label></div>`;

                Swal.fire({
                    title: 'Are you sure you want to proceed?',
                    html: htmlContent,
                    showCancelButton: true,
                    confirmButtonText: 'Confirm',
                    focusConfirm: false,
                    preConfirm: () => {
                        const checkbox = Swal.getPopup().querySelector('#confirmCharge');
                        if(!checkbox.checked){
                            Swal.showValidationMessage('Please confirm the charge.');
                            return false;
                        }
                        let selectedOpt = null;
                        const radio = Swal.getPopup().querySelector('input[name="paymentOpt"]:checked');
                        if(radio){
                            selectedOpt = radio.value;
                        } else if(optsArray.length === 1){
                            selectedOpt = Array.isArray(optsArray[0]) ? optsArray[0][0] : optsArray[0];
                        }
                        return {payment_option: selectedOpt};
                    },
                    customClass: {
                        popup: 'swal-success',
                        htmlContainer: 'text-start'
                    }
                }).then((result)=>{
                    if(result.isConfirmed){
                        this.paymentConfirmed = true;
                        this.selectedPaymentOption = result.value ? result.value.payment_option : null;
                        // Proceed with save and push after confirmation
                        this.saveAndPush();
                    }
                });
            },
            pushSegmentToPathway() {
                this.busy = true;
                //See if there is a census_connection set up
                if(this.isModeledAudience() || (this.local_census_connection.connection_id == this.pathway_selected.id && this.segment_selected.source ==
                    this.local_census_connection.source && this.segment_selected.data_source_id ==
                    this.local_census_connection.data_source_id)) {

                    let cleaned_mappings = [];
                    // Clean up mappings - convert object source to source.value 
                    if(this.isFile() && this.$refs.pathway_summary && this.$refs.pathway_summary.$refs.file_mapping) {
                        // For file exports, get mappings from the file mapping component
                        cleaned_mappings = this.$refs.pathway_summary.$refs.file_mapping.mapping || [];
                    } else if (this.local_census_connection.mappings) {

                        this.local_census_connection.mappings.forEach(mapping => {
                            if (mapping.source_column && typeof mapping.source_column.value === 'object') {
                                mapping.source_column.value = mapping.source_column.value.value;
                            }
                            if (mapping.destination_column && typeof mapping.destination_column === 'object') {
                                mapping.destination_column = mapping.destination_column.value;
                            }
                            cleaned_mappings.push(mapping);
                        });
                    }

                    var form_data = {
                        client_id: this.client.id,
                        segment_id: this.segment_selected.id,
                        census_connection_id: this.local_census_connection.id,
                        prioritization_column: this.local_census_connection.prioritization_column,
                        mappings: cleaned_mappings,
                        active: true,
                        payment_option: this.selectedPaymentOption,
                        ignore_filter: this.$refs.pathway_summary.getIfExcludeFilter()
                    }

                    //Call the webhook to save the new sync
                    window.axios.post('/api/census/add_sync', form_data)
                        .then(response => {
                            //If it is a file export...
                            if(this.pathway_selected.integration_id == "71b0c3b0-8390-49ec-9984-1c04b7384d38") {
                                //Make the sweetalert fire using the returned url as a "download" button
                                Swal.fire({
                                    title: 'Your File is Ready',
                                    text: this.segment_selected.name+ ' has been exported to a file and is ready to download.',
                                    icon: 'success',
                                    confirmButtonText: "Download",
                                    customClass: {
                                        popup: 'swal-success'
                                    },
                                }).then((result) => {
                                    this.busy = false;
                                    var newURL = '/pathways/'+this.client.url+"/push_audience";
                                    window.history.replaceState({}, {}, newURL);
                                    //Open a new tab and redirect to response.data.url
                                    window.open(response.data.url, '_blank');
                                    location.reload()
                                });
                            }
                            else {
                                Swal.fire({
                                    title: 'New Pathway Activated',
                                    text: this.segment_selected.name+ ' is being pushed to '+ this.pathway_selected.name,
                                    icon: 'success',
                                    customClass: {
                                        popup: 'swal-success'
                                    },
                                }).then((result) => {
                                    this.busy = false;
                                    var newURL = '/pathways/'+this.client.url+"/push_audience";
                                    window.history.replaceState({}, {}, newURL);
                                    location.reload()
                                });
                            }

                        }).catch(error => {
                            this.busy = false;
                            if(error.response.status == 400)
                                //Fields weren't mapped, time to redirect....
                                document.location.href = '/pathways/' + this.client.url + '/' + this.pathway_selected.id + '/mapping/' + this.segment_selected.source + '/' + this.segment_selected.data_source_id;
                            if(error.response.status == 403) {
                                Swal.fire({
                                    title: "Access Denied",
                                    text: "You don't have permission to perform this action. Please submit a request ticket if you feel this is incorrect.",
                                    icon: 'error',
                                    showCancelButton: false,
                                    customClass: {
                                        popup: 'swal-error'
                                    },
                                });
                            }
                            if(error.response.status == 500) {
                                if(error.response.data.census_error != null){
                                    this.local_census_connection.status_msg = error.response.data.census_error;
                                    Swal.fire({
                                        title: "Error On Syncing",
                                        text: this.local_census_connection.status_msg,
                                        icon: 'error',
                                        showCancelButton: false,
                                        customClass: {
                                            popup: 'swal-error'
                                        },
                                    }).then((result) => {
                                      if(this.local_census_connection.status_msg == 'The Census destination has been deleted') {
                                        location.reload()
                                      }
                                    });
                                }
                                if(error.response.data.sync_error != null)
                                    this.local_census_connection.syncs.push({
                                        last_sync_status: error.response.data.sync_error,
                                        client_id: this.client.id,
                                        segment_id: this.segment_selected.id,
                                        census_connection_id: this.local_census_connection.id
                                    });
                            }
                        });
                    return;
                }

                //If not, redirect to the mapping page
                document.location.href = '/pathways/' + this.client.url + '/' + this.pathway_selected.id + '/mapping/' + this.segment_selected.source + '/' + this.segment_selected.data_source_id;
            },
            save(and_sync = false) {
                var self = this;
                this.busy = true;

                // Clean up mappings - convert object source to source.value 
                let cleaned_mappings = [];   
                if (this.local_census_connection.mappings) {

                    this.local_census_connection.mappings.forEach(mapping => {
                        if (mapping.source_column && typeof mapping.source_column.value === 'object') {
                            mapping.source_column.value = mapping.source_column.value.value;
                        }
                        if (mapping.destination_column && typeof mapping.destination_column === 'object') {
                            mapping.destination_column = mapping.destination_column.value;
                        }
                        cleaned_mappings.push(mapping);
                    });
                }
                let cleaned_prioritization_columns = [];
                if (this.local_census_connection.prioritization_column) {
                    this.local_census_connection.prioritization_column.forEach(column => {
                        if (column.column && typeof column.column === 'object') {
                            column.column = column.column.value;
                        }
                        cleaned_prioritization_columns.push(column);
                    });
                }
                this.local_census_connection.mappings = cleaned_mappings;
                this.local_census_connection.prioritization_column = cleaned_prioritization_columns;

                if(this.isModeledAudience()) {
                    this.local_census_connection.operation = 'mirror';
                    this.local_census_connection.destination_object = 'model';
                }
                
                if(!this.local_census_connection.id) {
                    window.axios.post('/api/census_connection', this.local_census_connection)
                    .then(response => {
                        self.busy = false;
                        self.local_census_connection = response.data.census_connection;


                        if(and_sync){
                            self.pushSegmentToPathway();
                        }
                    });
                }
                else {
                    if(this.ifCensusConnectionChanged()){
                        this.local_census_connection.save_mapping = 0;
                        // call update api when there is any actual changes
                        window.axios.put('/api/census_connection/' + this.local_census_connection.id, this.local_census_connection)
                        .then(response => {
                            self.busy = false;
                            self.local_census_connection = response.data.census_connection

                            if(and_sync)
                                self.pushSegmentToPathway();
                        });
                    } else if(and_sync)
                        this.pushSegmentToPathway();
                }
            },
            ifCensusConnectionChanged() {
                const storedDest = this.stored_census_connection.destination_object;
                const localDest = this.local_census_connection.destination_object;
                const storedEmpty = storedDest == null || storedDest === '';
                const localEmpty = localDest == null || localDest === '';
                if (storedEmpty !== localEmpty)
                    return true;
                if (!storedEmpty && !localEmpty) {
                    const storedId = typeof storedDest === 'object' && storedDest !== null ? storedDest.connection_id : storedDest;
                    const localId = typeof localDest === 'object' && localDest !== null ? localDest.connection_id : localDest;
                    if (storedId !== localId)
                        return true;
                }
                if (this.stored_census_connection.operation != this.local_census_connection.operation)
                    return true;

                const stored = this.stored_census_connection;
                const local = this.local_census_connection;

                if (stored.prioritization_column == null || local.prioritization_column == null ||
                    stored.prioritization_column.length != local.prioritization_column.length) {
                    return true;
                } else {
                    // Create copies of the arrays and sort them for comparison
                    var old_value = stored.prioritization_column ? stored.prioritization_column.slice().sort((a, b) => (a.column > b.column) ? 1 : -1) : [];
                    var new_value = local.prioritization_column ? local.prioritization_column.slice().sort((a, b) => (a.column > b.column) ? 1 : -1) : [];
                    for (var i = 0; i < old_value.length; i++) {
                        if (old_value[i].column != new_value[i].column || old_value[i].sort != new_value[i].sort)
                            return true;
                    }
                }

                if (stored.mappings == null || local.mappings == null || local.mappings.length == 0 || stored.mappings.length != local.mappings.length) {
                    return true;
                } else {
                    // Create copies of the arrays and sort them for comparison
                    var old_value = stored.mappings.slice().sort((a, b) => (a.id > b.id) ? 1 : -1);
                    var new_value = local.mappings.slice().sort((a, b) => (a.id > b.id) ? 1 : -1);
                    for (var i = 0; i < old_value.length; i++) {
                        if (old_value[i].source_column.toString() != new_value[i].source_column.toString()
                            || old_value[i].destination_column != new_value[i].destination_column)
                            return true;
                    }
                }

                return false;
            },
            calculatePathwaysListHeight() {
                // Card structure:
                // - Card padding: 1rem (16px)
                // - Card body padding: 0
                // - Content height: ~60px (based on avatar and text)
                // - Gap between cards: 1rem (16px)
                const cardHeight = 92; // 16px padding + 60px content + 16px gap
                
                // Calculate height based on number of cards
                const cardsHeight = this.connection_datasets.length * cardHeight;
                
                return  cardsHeight;
            },
            allColumnsMapped() {
                if(!this.local_census_connection?.mappings || this.local_census_connection.mappings.length === 0){
                    return false;
                }

                // Check if the first mapping is a primary identifier and has a valid value
                var unmappedFields = this.local_census_connection.mappings.filter((mapping, index) =>
                    // If the first one isn't a primary identifier
                    (index == 0 && mapping.is_primary_identifier != 1) ||
                    // If the first one is an array but the first value isn't set
                    (index == 0 && Array.isArray(mapping.source_column) && mapping.source_column.length > 0 && 
                        (mapping.source_column[0].value == null || mapping.source_column[0].value == "")) ||
                    // If it is a normal object but the value hasn't been set
                    (mapping.source_column && !Array.isArray(mapping.source_column) && 
                        (mapping.source_column.value == null || mapping.source_column.value == ""))
                );
                return unmappedFields.length === 0;
            },
        }
    });
</script>
